import React from 'react';

import { styled } from 'styled-components';

import API from 'utils/api/api';
import Logger from 'utils/logger';

import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';
import { UuidableName } from 'utils/api/types';
import { Box } from '@mui/material';

export const StyledOption = styled.li`
  font-size: 16px;
  min-height: 24px !important;
`;

const PipelinePicker = ({
  value,
  onChange,
  fullWidth = false,
  placeholder = 'All pipelines',
  customHeight = '44px',
  initialShouldLoad = false,
}: {
  value: UuidableName | string | null;
  onChange: (value: UuidableName | null) => void;
  fullWidth?: boolean;
  placeholder?: string;
  customHeight?: string;
  initialShouldLoad?: boolean;
}): React.ReactElement => {
  const [shouldLoad, setShouldLoad] = React.useState(initialShouldLoad);
  const [selected, setSelected] = React.useState<UuidableName | null>(null);
  const { results, loading, loadingMore, hasMore, loadMore, searchQuery } = useInfiniteSearch(
    API.entities.searchEntityPipelines,
    shouldLoad
  );

  const selectedValue =
    typeof value === 'string' ? [selected, ...results].find((option) => option?.uuid === value) : value;

  React.useEffect(() => {
    if (typeof value !== 'string') {
      setSelected(null);
      return undefined;
    }
    if (selected?.uuid === value) {
      return undefined;
    }
    let cancelled = false;
    API.entities
      .searchEntityPipelines({ uuid: value })()
      .then(({ data }) => {
        if (!cancelled) {
          setSelected(data[0] ?? null);
        }
      })
      .catch((error) => Logger.warn('Could not load the selected pipeline', error));
    return () => {
      cancelled = true;
    };
  }, [value]);

  return (
    <Box sx={{ width: fullWidth ? '100%' : '400px', marginRight: '8px', height: customHeight }}>
      <InfiniteAutocomplete
        value={selectedValue || null}
        options={results}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        id="pipeline-dropdown-field"
        data-testid="pipeline-dropdown-field"
        onOpen={() => {
          setShouldLoad(true);
        }}
        onChange={(_, rawValue: UuidableName | null) => {
          if (rawValue?.uuid === null) {
            searchQuery('');
            return;
          }
          setSelected(rawValue || null);
          onChange(rawValue || null);
        }}
        onInputChange={(_, newInputValue, reason) => {
          searchQuery(reason === 'input' ? newInputValue : '');
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selectedOption) => option.uuid === selectedOption.uuid}
        renderOption={(props, option: UuidableName) => <StyledOption {...props}>{option.name}</StyledOption>}
        renderInput={(params) => <StyledTextField {...params} placeholder={placeholder} />}
        fullWidth
        size="small"
        style={{
          height: customHeight,
        }}
        sx={{
          '& .MuiInputBase-root': {
            height: customHeight,
          },
        }}
      />
    </Box>
  );
};

export default PipelinePicker;
